import React, { useState, useEffect } from 'react';
import { Card, DonutChart, Flex, Metric, Text } from '@tremor/react';
import { formatTime } from '../../utils/calcs';
import { getDeterministicSpeakerColors } from '../../utils/speakerColors';

function OverallParticipationCard({ duration, fragment, t, speakerNames, ...rest }) {
    const [participationData, setParticipationData] = useState([]);

    useEffect(() => {
        // Function to replace keys with speaker names if available
        const replaceKeysWithSpeakerNames = (entries) => {
            return entries
                .filter(([key, value]) => {
                    // Filter out invalid entries that could cause undefined values
                    if (!key || key === null || key === undefined) {
                        console.error('❌ PARTICIPATION CARD - Invalid key:', key);
                        return false;
                    }
                    if (value === null || value === undefined || value === '') {
                        console.error('❌ PARTICIPATION CARD - Invalid value for key', key, ':', value);
                        return false;
                    }
                    const parsedValue = parseFloat(value);
                    if (isNaN(parsedValue)) {
                        console.error('❌ PARTICIPATION CARD - Cannot parse value for key', key, ':', value);
                        return false;
                    }
                    return true;
                })
                .map(([key, value]) => {
                    // Replace the key with the speaker name if it exists in speakerNames
                    let name = speakerNames && speakerNames[key] ? speakerNames[key] : key;
                    
                    // Additional validation for the final name
                    if (!name || name === null || name === undefined || name === '') {
                        console.error('❌ PARTICIPATION CARD - Final name is invalid for key', key, '- using key as fallback');
                        name = key || 'Unknown Speaker';
                    }
                    
                    const parsedValue = parseFloat(value);
                    console.log('✅ PARTICIPATION CARD - Valid entry:', { name, value: parsedValue });
                    
                    return { name, value: parsedValue };
                });
        };

        if (fragment?.result) {
            try {
                const parsedResult = JSON.parse(fragment.result);
                if (parsedResult && typeof parsedResult === 'object') {
                    const entries = Object.entries(parsedResult);
                    const replacedEntries = replaceKeysWithSpeakerNames(entries);
                    setParticipationData(replacedEntries);
                }
            } catch (error) {
                console.error('Error parsing participation data:', error);
                setParticipationData([]);
            }
        }
    }, [fragment?.result, speakerNames]);

    // Comprehensive 50-color palette to prevent color repetition in large datasets
    const colors = [
        // Primary colors (1-10)
        "emerald-500", "blue-500", "purple-500", "red-500", "yellow-500", 
        "lime-500", "pink-500", "indigo-500", "orange-500", "teal-500",
        
        // Secondary vibrant colors (11-20)
        "cyan-500", "violet-500", "rose-500", "amber-500", "green-500",
        "sky-500", "fuchsia-500", "orange-400", "emerald-600", "blue-600",
        
        // Darker variants (21-30)
        "purple-600", "red-600", "yellow-600", "lime-600", "pink-600",
        "indigo-600", "teal-600", "cyan-600", "violet-600", "rose-600",
        
        // Lighter variants (31-40)
        "emerald-400", "blue-400", "purple-400", "red-400", "yellow-400",
        "lime-400", "pink-400", "indigo-400", "orange-300", "teal-400",
        
        // Additional unique colors (41-50)
        "slate-500", "gray-500", "zinc-500", "neutral-500", "stone-500",
        "amber-600", "green-600", "sky-600", "fuchsia-600", "rose-700"
    ];

    return (
        <Card className='flex flex-col h-full justify-between max-h-96' data-testid="overall-participation-card" {...rest}>
            {fragment.result ?
                <>
                    <div>
                        <Flex alignItems="start">
                            <Text>{t('ExpandedMeedingCard.TimeSpokenLabel')}</Text>
                        </Flex>
                        <Flex className="space-x-3 truncate" justifyContent="start" alignItems="baseline">
                            <Metric>{formatTime(duration)}</Metric>
                        </Flex>
                    </div>
                    <div>
                        <DonutChart
                            className="mt-6 max-h-32"
                            data={participationData}
                            category="value"
                            index="name"
                            colors={getDeterministicSpeakerColors(participationData.map(entry => entry.name))}
                            showAnimation={true}
                            showLabel={false}
                        />
                    </div>
                    <div className='w-full flex justify-center items-center'>
                        {(() => {
                            console.error('🚨🚨🚨 OVERALL PARTICIPATION CARD LEGEND RENDERING 🚨🚨🚨');
                            console.error('📊 Participation data:', participationData);
                            console.error('🎨 Available colors:', colors);
                            
                            if (!Array.isArray(participationData) || participationData.length === 0) {
                                console.error('❌ OVERALL PARTICIPATION CARD - Invalid or empty participation data');
                                return <div className="text-red-500 italic">No participation data available</div>;
                            }
                            
                            // Additional validation to filter out entries with undefined/invalid values
                            const validEntries = participationData.filter(entry => {
                                if (!entry || typeof entry !== 'object') {
                                    console.error('❌ PARTICIPATION CARD LEGEND - Invalid entry object:', entry);
                                    return false;
                                }
                                if (!entry.name || entry.name === null || entry.name === undefined || entry.name === '') {
                                    console.error('❌ PARTICIPATION CARD LEGEND - Invalid name:', entry.name, 'for entry:', entry);
                                    return false;
                                }
                                if (entry.value === null || entry.value === undefined || isNaN(entry.value)) {
                                    console.error('❌ PARTICIPATION CARD LEGEND - Invalid value:', entry.value, 'for entry:', entry);
                                    return false;
                                }
                                console.log('✅ PARTICIPATION CARD LEGEND - Valid entry:', entry);
                                return true;
                            });
                            
                            if (validEntries.length === 0) {
                                console.error('❌ PARTICIPATION CARD LEGEND - No valid entries after filtering');
                                return <div className="text-red-500 italic">No valid participation data for legend</div>;
                            }
                            
                            console.error('📊 PARTICIPATION CARD LEGEND - Filtered valid entries:', validEntries);
                            
                            const categories = validEntries.map(entry => {
                                const category = `${entry.name} (${entry.value}%)`;
                                console.error('🏷️ OVERALL PARTICIPATION CARD Category:', category);
                                
                                // Final check that category doesn't contain "undefined"
                                if (category.includes('undefined') || category.includes('null')) {
                                    console.error('❌ PARTICIPATION CARD LEGEND - Category contains undefined/null:', category);
                                    return `${entry.name || 'Unknown'} (${entry.value || 0}%)`;
                                }
                                
                                return category;
                            });
                            
                            // Use deterministic colors based on speaker names for consistency across components
                            const speakerNames = validEntries.map(entry => entry.name);
                            const assignedColors = getDeterministicSpeakerColors(speakerNames);
                            
                            console.error('🎨 OVERALL PARTICIPATION CARD Assigned colors:', assignedColors);
                            console.error(`📊 Color assignment: ${validEntries.length} entries, ${colors.length} unique colors available`);
                            
                            // Final validation that no categories or colors are undefined
                            const hasUndefinedCategories = categories.some(cat => cat === undefined || cat === null || cat.includes('undefined'));
                            const hasUndefinedColors = assignedColors.some(color => color === undefined || color === null);
                            
                            if (hasUndefinedCategories) {
                                console.error('❌ PARTICIPATION CARD LEGEND - Categories contain undefined values:', categories);
                                return <div className="text-red-500 italic">Legend categories contain invalid data</div>;
                            }
                            
                            if (hasUndefinedColors) {
                                console.error('❌ PARTICIPATION CARD LEGEND - Colors contain undefined values:', assignedColors);
                                return <div className="text-red-500 italic">Legend colors contain invalid data</div>;
                            }
                            
                            console.error('✅ OVERALL PARTICIPATION CARD - Rendering Legend');
                            
                            // Use custom legend instead of Tremor Legend to prevent Chart.js errors
                            console.error('✅ PARTICIPATION CARD - Using CUSTOM LEGEND to bypass Tremor/Chart.js issues');
                            
                            return (
                                <div className="mt-3 max-h-32 overflow-y-auto">
                                    <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 px-2">
                                        {validEntries.map((entry, index) => (
                                            <div key={index} className="flex items-center gap-2 flex-shrink-0">
                                                <div 
                                                    className={`w-3 h-3 rounded bg-${assignedColors[index] || 'gray-500'}`}
                                                ></div>
                                                <span className="text-sm text-gray-700">
                                                    {entry.name} ({entry.value}%)
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </>
                : <div className='min-w-full h-32 bg-gray-200 animate-pulse'></div>}
        </Card>
    );
}

export default OverallParticipationCard;
